import React, { useEffect } from 'react';

import { userType } from '@atlassiansox/analytics-web-client/analytics-web-types';
import { FeatureGateEnvironment } from '@atlaskit/feature-gate-js-client/types';
import {
	FeatureGatesInitialization,
	FeatureGatesInitializationWithValues,
} from '@atlassian/feature-gates-react';
import { setGlobalTheme } from '@atlaskit/tokens/set-global-theme';

import { getAccountIdForAnalytics, getSiteId, getStatsigValuesFromDOM } from './utils';
import { client } from './analytics-client';
import { initializeBrowserStorageControls } from '@atlassian/conversation-assistant-widget/browser-storage-controls';

const uuid = () => {
	try {
		return crypto.randomUUID();
	} catch {
		return '';
	}
};

const CAWI_SERVICE_CLIENT_SDK_KEY = 'client-GpdXVCigN7H6nTzlZh3cBcZrAJgnT3en18zvWXaFo0J';

/**
 * Fx3 API key for the iframe Statsig client.
 * @see {@link https://developer.atlassian.com/platform/frontend-feature-flags/resources/api-keys/}
 */
const getCawiServiceFx3ApiKey = (): string => {
	switch (process.env.CAWI_ENV) {
		case 'dev':
			return 'e5769d1e-85ca-47bf-9208-e722e46c76bb';
		case 'prod':
			return '59be15ae-3aff-40e2-bc2b-329e01934efd';
		default:
			return 'ad735a7c-ed84-4823-8747-12c854abc98b';
	}
};

const CAWI_SERVICE_FX3_API_KEY = getCawiServiceFx3ApiKey();
const RANDOMIZATION_ID: string = uuid();
const STATSIG_SERVER_VALUES = getStatsigValuesFromDOM();

export const AppBootstrap = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
	const options = {
		environment:
			process.env.CAWI_ENV === 'prod'
				? FeatureGateEnvironment.Production
				: FeatureGateEnvironment.Staging,
		targetApp: 'cawi-service_web',
	} as const;

	const onReady = (err?: Error) => {
		if (err) {
			console.error('[FAILED TO INITIALIZE STATSIG]', err);
		}
		/**
		 * Configure the global theme once we have initilized the feature gates client
		 * so it can leverage feature gates
		 */
		setGlobalTheme({
			colorMode: 'light',
		});

		// Initialize Browser Storage Controls inside the iframe
		void initializeBrowserStorageControls({
			cloudId: getSiteId(),
			userId: getAccountIdForAnalytics() ?? undefined,
		});
	};

	const identifiers = {
		tenantId: getSiteId(),
		randomizationId: RANDOMIZATION_ID,
		analyticsAnonymousId: client.getAnonymousId(),
	};

	useEffect(() => {
		const accountId = getAccountIdForAnalytics();
		if (accountId) {
			client.setUserInfo(userType.ATLASSIAN_ACCOUNT, accountId);
		}
	}, []);

	if (STATSIG_SERVER_VALUES) {
		return (
			<FeatureGatesInitializationWithValues
				initializeValues={STATSIG_SERVER_VALUES}
				identifiers={identifiers}
				options={{ sdkKey: CAWI_SERVICE_CLIENT_SDK_KEY, ...options }}
				onReady={onReady}
			>
				{children}
			</FeatureGatesInitializationWithValues>
		);
	}

	return (
		<FeatureGatesInitialization
			identifiers={identifiers}
			options={{ apiKey: CAWI_SERVICE_FX3_API_KEY, ...options }}
			onReady={onReady}
		>
			{children}
		</FeatureGatesInitialization>
	);
};
